import React, { useState, useEffect } from 'react';
import { billingService, tokenService } from '../../lib/services/billing/index.js';
import { useTranslation } from '../../lib/i18n';
import LanguageSwitcher from '../Common/LanguageSwitcher';
import './BillingDashboard.css';

const BillingDashboard = ({ currentTenant, onBack, onTabChange }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [billingPlans, setBillingPlans] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [tokenUsageStats, setTokenUsageStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({
    tokenAmount: 100,
    paymentMethod: 'credit_card'
  });

  // Mock user ID for demo purposes
  const userId = '550e8400-e29b-41d4-a716-446655440000';

  useEffect(() => {
    loadDashboardData();
  }, [currentTenant]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load billing plans
      const plansResult = await billingService.getBillingPlans(currentTenant);
      setBillingPlans(plansResult.data);

      // Load user subscriptions
      const subscriptionsResult = await billingService.getUserSubscriptions(userId, currentTenant);
      setUserSubscriptions(subscriptionsResult.data);

      // Load token balance
      const balanceResult = await tokenService.getUserTokenBalance(userId, currentTenant);
      setTokenBalance(balanceResult.data);

      // Load subscription stats
      const statsResult = await billingService.getSubscriptionStats(currentTenant);
      setSubscriptionStats(statsResult.data);

      // Load token usage stats
      const usageResult = await tokenService.getTokenUsageStats(currentTenant);
      setTokenUsageStats(usageResult.data);

    } catch (err) {
      setError(err.message);
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseTokens = async () => {
    if (!selectedPlan) return;
    
    setLoading(true);
    try {
      const costAmount = (purchaseForm.tokenAmount * 0.02).toFixed(2); // $0.02 per token
      
      await tokenService.purchaseTokens(
        userId,
        currentTenant,
        purchaseForm.tokenAmount,
        parseFloat(costAmount),
        'one_time',
        purchaseForm.paymentMethod
      );
      
      setShowPurchaseModal(false);
      setSelectedPlan(null);
      setPurchaseForm({ tokenAmount: 100, paymentMethod: 'credit_card' });
      
      // Reload data
      await loadDashboardData();
      
      alert('Token purchase successful!');
    } catch (err) {
      setError(err.message);
      console.error('Error purchasing tokens:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribeToPlan = async (planId) => {
    setLoading(true);
    try {
      await billingService.createSubscription(userId, planId, currentTenant);
      await loadDashboardData();
      alert('Subscription created successfully!');
    } catch (err) {
      setError(err.message);
      console.error('Error creating subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    
    setLoading(true);
    try {
      await billingService.cancelSubscription(subscriptionId);
      await loadDashboardData();
      alert('Subscription cancelled successfully!');
    } catch (err) {
      setError(err.message);
      console.error('Error cancelling subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !billingPlans.length) {
    return (
      <div className="billing-dashboard">
        <div className="loading">Loading billing dashboard...</div>
      </div>
    );
  }

  return (
    <div className="billing-dashboard">
      <div className="header">
        <button className="back-btn" onClick={onBack}>
          ← {t('common.back')}
        </button>
        <h1>💰 {t('billingDashboard.title')}</h1>
        <LanguageSwitcher />
      </div>

      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}

      <div className="tabs">
        <button 
          className={activeTab === 'overview' ? 'active' : ''} 
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={activeTab === 'plans' ? 'active' : ''} 
          onClick={() => setActiveTab('plans')}
        >
          📋 Billing Plans
        </button>
        <button 
          className={activeTab === 'subscriptions' ? 'active' : ''} 
          onClick={() => setActiveTab('subscriptions')}
        >
          🔄 Subscriptions
        </button>
        <button 
          className={activeTab === 'tokens' ? 'active' : ''} 
          onClick={() => setActiveTab('tokens')}
        >
          🪙 Token Management
        </button>
      </div>

      <div className="content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Token Balance</h3>
                <div className="stat-value">
                  {tokenBalance ? tokenBalance.tokens_remaining.toLocaleString() : '0'} tokens
                </div>
                <div className="stat-detail">
                  Used: {tokenBalance ? tokenBalance.tokens_used.toLocaleString() : '0'} / 
                  Total: {tokenBalance ? tokenBalance.tokens_allocated.toLocaleString() : '0'}
                </div>
              </div>

              <div className="stat-card">
                <h3>Active Subscriptions</h3>
                <div className="stat-value">
                  {subscriptionStats ? subscriptionStats.active_subscriptions : '0'}
                </div>
                <div className="stat-detail">
                  Total: {subscriptionStats ? subscriptionStats.total_subscriptions : '0'}
                </div>
              </div>

              <div className="stat-card">
                <h3>Monthly Usage</h3>
                <div className="stat-value">
                  {tokenUsageStats ? tokenUsageStats.total_tokens_consumed.toLocaleString() : '0'} tokens
                </div>
                <div className="stat-detail">
                  Cost: ${tokenUsageStats ? tokenUsageStats.total_cost.toFixed(2) : '0.00'}
                </div>
              </div>

              <div className="stat-card">
                <h3>Average Usage</h3>
                <div className="stat-value">
                  {tokenUsageStats ? Math.round(tokenUsageStats.average_tokens_per_usage) : '0'} tokens
                </div>
                <div className="stat-detail">
                  Per session
                </div>
              </div>
            </div>

            <div className="quick-actions">
              <h3>Quick Actions</h3>
              <div className="action-buttons">
                <button 
                  className="action-btn primary"
                  onClick={() => setActiveTab('tokens')}
                >
                  🪙 Purchase Tokens
                </button>
                <button 
                  className="action-btn secondary"
                  onClick={() => setActiveTab('plans')}
                >
                  📋 View Plans
                </button>
                <button 
                  className="action-btn secondary"
                  onClick={() => onTabChange('token-usage')}
                >
                  📈 Usage History
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'plans' && (
          <div className="plans-tab">
            <h2>Available Billing Plans</h2>
            
            <div className="plan-filters">
              <button 
                className="filter-btn active"
                onClick={() => setBillingPlans(billingPlans.filter(p => p.plan_type === 'school'))}
              >
                School Plans
              </button>
              <button 
                className="filter-btn"
                onClick={() => setBillingPlans(billingPlans.filter(p => p.plan_type === 'individual'))}
              >
                Individual Plans
              </button>
              <button 
                className="filter-btn"
                onClick={() => setBillingPlans(billingPlans.filter(p => p.plan_type === 'app_specific'))}
              >
                App-Specific Plans
              </button>
            </div>

            <div className="plans-grid">
              {billingPlans.map(plan => (
                <div key={plan.id} className="plan-card">
                  <div className="plan-header">
                    <h3>{plan.plan_name}</h3>
                    <div className="plan-type">{plan.plan_type}</div>
                  </div>
                  
                  <div className="plan-price">
                    <span className="price">${plan.monthly_fee}</span>
                    <span className="period">/month</span>
                  </div>
                  
                  <div className="plan-tokens">
                    {plan.token_allowance.toLocaleString()} tokens included
                  </div>
                  
                  <div className="plan-features">
                    {plan.features && Object.entries(plan.features).map(([key, value]) => (
                      <div key={key} className="feature">
                        <span className="feature-key">{key}:</span>
                        <span className="feature-value">{value}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    className="subscribe-btn"
                    onClick={() => handleSubscribeToPlan(plan.id)}
                    disabled={loading}
                  >
                    Subscribe Now
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <div className="subscriptions-tab">
            <h2>Your Subscriptions</h2>
            
            {userSubscriptions.length === 0 ? (
              <div className="no-subscriptions">
                <p>No active subscriptions found.</p>
                <button 
                  className="action-btn primary"
                  onClick={() => setActiveTab('plans')}
                >
                  Browse Plans
                </button>
              </div>
            ) : (
              <div className="subscriptions-list">
                {userSubscriptions.map(subscription => (
                  <div key={subscription.id} className="subscription-card">
                    <div className="subscription-header">
                      <h3>{subscription.billing_plans?.plan_name || 'Custom Plan'}</h3>
                      <span className={`status ${subscription.status}`}>
                        {subscription.status}
                      </span>
                    </div>
                    
                    <div className="subscription-details">
                      <div className="detail">
                        <span className="label">Start Date:</span>
                        <span className="value">{subscription.start_date}</span>
                      </div>
                      <div className="detail">
                        <span className="label">End Date:</span>
                        <span className="value">{subscription.end_date || 'Ongoing'}</span>
                      </div>
                      <div className="detail">
                        <span className="label">Tokens Used:</span>
                        <span className="value">
                          {subscription.tokens_used.toLocaleString()} / {subscription.tokens_allocated.toLocaleString()}
                        </span>
                      </div>
                      <div className="detail">
                        <span className="label">Remaining:</span>
                        <span className="value">{subscription.tokens_remaining.toLocaleString()} tokens</span>
                      </div>
                    </div>
                    
                    {subscription.status === 'active' && (
                      <div className="subscription-actions">
                        <button 
                          className="cancel-btn"
                          onClick={() => handleCancelSubscription(subscription.id)}
                          disabled={loading}
                        >
                          Cancel Subscription
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'tokens' && (
          <div className="tokens-tab">
            <h2>Token Management</h2>
            
            <div className="token-balance-card">
              <h3>Current Balance</h3>
              <div className="balance-display">
                <span className="balance-amount">
                  {tokenBalance ? tokenBalance.tokens_remaining.toLocaleString() : '0'}
                </span>
                <span className="balance-unit">tokens</span>
              </div>
              <div className="balance-details">
                <div className="balance-detail">
                  <span>Used:</span>
                  <span>{tokenBalance ? tokenBalance.tokens_used.toLocaleString() : '0'} tokens</span>
                </div>
                <div className="balance-detail">
                  <span>Total Allocated:</span>
                  <span>{tokenBalance ? tokenBalance.tokens_allocated.toLocaleString() : '0'} tokens</span>
                </div>
              </div>
            </div>

            <div className="token-purchase-section">
              <h3>Purchase Tokens</h3>
              <div className="purchase-options">
                <div className="purchase-option" onClick={() => {
                  setSelectedPlan({ tokenAmount: 100, cost: 2.00 });
                  setPurchaseForm({ ...purchaseForm, tokenAmount: 100 });
                  setShowPurchaseModal(true);
                }}>
                  <div className="option-tokens">100 tokens</div>
                  <div className="option-price">$2.00</div>
                </div>
                <div className="purchase-option" onClick={() => {
                  setSelectedPlan({ tokenAmount: 500, cost: 9.00 });
                  setPurchaseForm({ ...purchaseForm, tokenAmount: 500 });
                  setShowPurchaseModal(true);
                }}>
                  <div className="option-tokens">500 tokens</div>
                  <div className="option-price">$9.00</div>
                </div>
                <div className="purchase-option" onClick={() => {
                  setSelectedPlan({ tokenAmount: 1000, cost: 16.00 });
                  setPurchaseForm({ ...purchaseForm, tokenAmount: 1000 });
                  setShowPurchaseModal(true);
                }}>
                  <div className="option-tokens">1,000 tokens</div>
                  <div className="option-price">$16.00</div>
                </div>
                <div className="purchase-option" onClick={() => {
                  setSelectedPlan({ tokenAmount: 2500, cost: 35.00 });
                  setPurchaseForm({ ...purchaseForm, tokenAmount: 2500 });
                  setShowPurchaseModal(true);
                }}>
                  <div className="option-tokens">2,500 tokens</div>
                  <div className="option-price">$35.00</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Purchase Tokens</h3>
              <button 
                className="close-btn"
                onClick={() => setShowPurchaseModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="purchase-summary">
                <div className="summary-item">
                  <span>Tokens:</span>
                  <span>{purchaseForm.tokenAmount.toLocaleString()}</span>
                </div>
                <div className="summary-item">
                  <span>Cost:</span>
                  <span>${(purchaseForm.tokenAmount * 0.02).toFixed(2)}</span>
                </div>
              </div>
              
              <div className="form-group">
                <label>Payment Method:</label>
                <select 
                  value={purchaseForm.paymentMethod}
                  onChange={(e) => setPurchaseForm({
                    ...purchaseForm,
                    paymentMethod: e.target.value
                  })}
                >
                  <option value="credit_card">Credit Card</option>
                  <option value="paypal">PayPal</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowPurchaseModal(false)}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn"
                onClick={handlePurchaseTokens}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Confirm Purchase'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingDashboard;
